import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { MealProduct, TreatProduct, SupplementProduct, ExtraProduct } from '@repo/api-types'
import { useProducts } from '@/composables/useProducts'
import { compareExtrasProducts, isProductVisible } from '@/utils/product'

export const useProductStore = defineStore('products', () => {
  const loading = ref(false)

  const meals = ref<MealProduct[]>([])
  const treats = ref<TreatProduct[]>([])
  const supplements = ref<SupplementProduct[]>([])

  // Only show visible products in the store
  const visibleMeals = computed(() => meals.value.filter(isProductVisible))
  const visibleTreats = computed(() => treats.value.filter(isProductVisible))
  const visibleSupplements = computed(() => supplements.value.filter(isProductVisible))

  const load = async () => {
    loading.value = true
    const { products, error } = await useProducts().getProducts()
    if (error) {
      console.error(error)
      loading.value = false
      return
    }
    if (!products) {
      console.error('No products loaded')
      loading.value = false
      return
    }

    meals.value = products.meals
    treats.value = products.treats.sort(compareExtrasProducts)
    supplements.value = products.supplements.sort(compareExtrasProducts)

    loading.value = false
  }

  /**
   * A note on the "product.isVisible" attribute:
   *
   * In general, the business works as follows:
   * - we decide to deprecate a product
   * - we mark it as "out of stock" which means customers cannot add it to their order, but customers with it in their order can manage it
   * - we then send out comms and remove that product from the customers after a certain amount of time
   * So, what that means is that we should never be in a position where the product is "not visible" and is on a future order.
   */

  /** Get the meal product by variant ID
   * @param id - The variant ID
   * @param allowInvisible - Whether to allow invisible products (default: false) Enable this when showing an order line item. Disable when listing products for selection.
   * @returns The meal product or undefined if not found
   */
  const getMealByVariantId = (id: string, { allowInvisible = false } = {}): MealProduct | undefined => {
    return meals.value.find(
      (meal) =>
        (meal.isVisible || allowInvisible) &&
        meal.variants.some((variant) => variant.id === id && (variant.isVisible || allowInvisible)),
    )
  }

  const getMealById = (id: string, { allowInvisible = false } = {}): MealProduct | undefined => {
    return meals.value.find((meal) => (meal.isVisible || allowInvisible) && meal.id === id)
  }

  /** Get a meal product by its product code (e.g. "beef", "chicken").
   * Product codes align with the meal slugs returned by the recommendation API.
   */
  const getMealByProductCode = (productCode: string, { allowInvisible = false } = {}): MealProduct | undefined => {
    return meals.value.find((meal) => (meal.isVisible || allowInvisible) && meal.productCode === productCode)
  }

  /** Get the extra product (treat or supplement) by variant ID
   * @param id - The variant ID
   * @param allowInvisible - Whether to allow invisible products (default: false) Enable this when showing an order line item. Disable when listing products for selection.
   * @returns The extra product or undefined if not found
   */
  const getExtraByVariantId = (id: string, { allowInvisible = false } = {}): ExtraProduct | undefined => {
    const extras: ExtraProduct[] = [...treats.value, ...supplements.value]
    return extras.find(
      (extra) =>
        (extra.isVisible || allowInvisible) &&
        extra.variants.some((variant) => variant.id === id && (variant.isVisible || allowInvisible)),
    )
  }

  return {
    loading,
    meals: visibleMeals,
    treats: visibleTreats,
    supplements: visibleSupplements,
    getMealById,
    getMealByVariantId,
    getMealByProductCode,
    getExtraByVariantId,
    load,
  }
})
